import React from 'react';
import { GlassCard, GlassBadge } from '@/design-system';
import { AlertTriangle, Clock, ExternalLink, Link2 } from 'lucide-react';

interface DependencyAtRisk {
  id: string;
  title: string;
  description: string;
  severity: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
  status: 'OPEN' | 'IN_PROGRESS' | 'RESOLVED' | 'CANCELLED';
  dependentEntity: {
    type: string;
    name: string;
    id: string;
  };
  blockedEntity: {
    type: string;
    name: string;
    id: string;
  };
  dueDate?: string;
  createdAt: string;
}

interface DependenciesAtRiskListProps {
  dependencies: DependencyAtRisk[] | null;
  isLoading: boolean;
}

const DependenciesAtRiskList: React.FC<DependenciesAtRiskListProps> = ({
  dependencies,
  isLoading,
}) => {
  const getSeverityBadge = (severity: string) => {
    switch (severity) {
      case 'CRITICAL':
        return <GlassBadge variant="danger">Critical</GlassBadge>;
      case 'HIGH':
        return <GlassBadge variant="warning">High</GlassBadge>;
      case 'MEDIUM':
        return <GlassBadge variant="info">Medium</GlassBadge>;
      case 'LOW':
        return <GlassBadge variant="default">Low</GlassBadge>;
      default:
        return <GlassBadge variant="default">{severity}</GlassBadge>;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'OPEN':
        return <GlassBadge variant="danger">Open</GlassBadge>;
      case 'IN_PROGRESS':
        return <GlassBadge variant="warning">In Progress</GlassBadge>;
      case 'RESOLVED':
        return <GlassBadge variant="success">Resolved</GlassBadge>;
      case 'CANCELLED':
        return <GlassBadge variant="default">Cancelled</GlassBadge>;
      default:
        return <GlassBadge variant="default">{status}</GlassBadge>;
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'CRITICAL':
        return <AlertTriangle className="w-5 h-5 text-status-error" />;
      case 'HIGH':
        return <AlertTriangle className="w-5 h-5 text-status-warning" />;
      default:
        return <Clock className="w-5 h-5 text-status-info" />;
    }
  };

  const isOverdue = (dueDate?: string) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date();
  };

  const getDaysUntilDue = (dueDate?: string) => {
    if (!dueDate) return null;
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = due.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (isLoading) {
    return (
      <GlassCard className="p-6 rounded-2xl">
        <div className="animate-pulse">
          <div className="h-6 bg-muted/50 dark:bg-muted/30 rounded w-1/2 mb-4"></div>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex items-center space-x-4 p-4 bg-surface-muted/50 rounded-lg">
                <div className="w-10 h-10 bg-muted/50 dark:bg-muted/30 rounded-full"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-muted/50 dark:bg-muted/30 rounded w-3/4"></div>
                  <div className="h-3 bg-muted/50 dark:bg-muted/30 rounded w-1/2"></div>
                </div>
                <div className="w-20 h-6 bg-muted/50 dark:bg-muted/30 rounded"></div>
              </div>
            ))}
          </div>
        </div>
      </GlassCard>
    );
  }

  if (!dependencies || dependencies.length === 0) {
    return (
      <GlassCard className="p-6 rounded-2xl">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-status-success to-teal-600 flex items-center justify-center shadow-lg">
            <Link2 className="w-5 h-5 text-text-inverse" />
          </div>
          <h3 className="text-lg font-semibold text-foreground">Dependencies at Risk</h3>
        </div>
        <div className="text-center text-muted-foreground py-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-status-success-bg flex items-center justify-center">
            <AlertTriangle className="w-8 h-8 text-status-success" />
          </div>
          <p className="font-medium text-foreground">No dependencies at risk</p>
          <p className="text-sm mt-1">All dependencies are on track</p>
        </div>
      </GlassCard>
    );
  }

  const criticalDependencies = dependencies.filter(d => d.severity === 'CRITICAL');
  const highDependencies = dependencies.filter(d => d.severity === 'HIGH');

  return (
    <GlassCard className="p-6 rounded-2xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-status-error to-pink-600 flex items-center justify-center shadow-lg">
            <Link2 className="w-5 h-5 text-text-inverse" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Dependencies at Risk</h3>
            <p className="text-sm text-muted-foreground">
              Requiring immediate attention
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-4">
          <div className="text-center">
            <div className="text-lg font-bold text-status-error">
              {criticalDependencies.length}
            </div>
            <div className="text-xs text-muted-foreground">Critical</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-status-warning">
              {highDependencies.length}
            </div>
            <div className="text-xs text-muted-foreground">High</div>
          </div>
        </div>
      </div>

      {/* Dependency List */}
      <div className="space-y-3">
        {dependencies
          .sort((a, b) => {
            const severityOrder = { CRITICAL: 0, HIGH: 1, MEDIUM: 2, LOW: 3 };
            return severityOrder[a.severity as keyof typeof severityOrder] -
                   severityOrder[b.severity as keyof typeof severityOrder];
          })
          .slice(0, 5)
          .map((dependency) => {
            const daysUntilDue = getDaysUntilDue(dependency.dueDate);
            const overdue = isOverdue(dependency.dueDate);

            return (
              <div
                key={dependency.id}
                className={`p-4 rounded-xl border transition-all hover:shadow-md ${
                  dependency.severity === 'CRITICAL'
                    ? 'bg-status-error-bg/50 border-status-error/30'
                    : 'bg-surface-muted/50 border-border'
                }`}
              >
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 mt-0.5">
                    {getSeverityIcon(dependency.severity)}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center flex-wrap gap-2 mb-2">
                      <h4 className="font-medium text-foreground truncate">
                        {dependency.title}
                      </h4>
                      {getSeverityBadge(dependency.severity)}
                      {getStatusBadge(dependency.status)}
                    </div>

                    <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                      {dependency.description}
                    </p>

                    <div className="flex flex-wrap gap-4 text-xs">
                      <div>
                        <span className="text-muted-foreground">Blocking: </span>
                        <span className="text-foreground font-medium">
                          {dependency.blockedEntity.name}
                        </span>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Depends on: </span>
                        <span className="text-foreground font-medium">
                          {dependency.dependentEntity.name}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex-shrink-0 text-right">
                    {dependency.dueDate && (
                      <div className="mb-2">
                        <div className={`text-sm font-medium ${
                          overdue ? 'text-status-error' :
                          daysUntilDue !== null && daysUntilDue <= 7 ? 'text-status-warning' : 'text-foreground'
                        }`}>
                          {overdue ? 'Overdue' :
                           daysUntilDue !== null ? `${daysUntilDue} days` : 'No due date'}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Due: {new Date(dependency.dueDate).toLocaleDateString()}
                        </div>
                      </div>
                    )}

                    <button className="flex items-center gap-1 text-accent-blue hover:text-accent-blue-hover text-sm font-medium transition-colors">
                      <span>View</span>
                      <ExternalLink className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
      </div>

      {/* Summary Actions */}
      {dependencies.length > 0 && (
        <div className="mt-6 pt-4 border-t border-border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground">{criticalDependencies.length + highDependencies.length}</span> dependencies need immediate attention
              </p>
            </div>
            <div className="flex space-x-2">
              {criticalDependencies.length > 0 && (
                <button className="px-3 py-1.5 bg-status-error hover:bg-status-error-hover text-text-inverse text-sm font-medium rounded-lg transition-colors">
                  Escalate Critical
                </button>
              )}
              <button className="px-3 py-1.5 bg-surface-muted hover:bg-bg-hover text-foreground text-sm font-medium rounded-lg transition-colors">
                View All
              </button>
            </div>
          </div>
        </div>
      )}
    </GlassCard>
  );
};

export default DependenciesAtRiskList;
